import type { GrammarTopic } from './types';

export const foundationsTopics: GrammarTopic[] = [
  {
    slug: 'glossary-of-grammar-terms',
    title: 'Glossary of Grammar Terms',
    blocks: [
      {
        kind: 'p',
        text: 'The following glossary presents some basic grammar terms used throughout this material. These definitions are provided to help you understand them.',
      },
      {
        kind: 'list',
        items: [
          'Subject: The person or thing that does the action in a sentence. Example: Maria **studies** English.',
          'Verb: A word that expresses an action, event, or state. e.g. I **like** to read books in French.',
          'Base form: The basic form of a verb, without “to” and without any endings. e.g. I can’t **go** to the restaurant today.',
          'Infinitive (to + verb): The form of a verb that begins with to. e.g. I need **to study** German.',
          'Object: The person or thing that receives the action of the verb. e.g. She reads **a book**.',
          'Gerund: The -ing form of a verb used as a noun. e.g. **Swimming** is good exercise.',
          'Continuous (or Progressive): A verb form used to describe actions that are happening now or around the present moment. It is formed with be + verb + -ing. e.g. She **is studying**.',
          'Perfect: A verb form used to connect past actions with the present or another moment in time. It is formed with have + past participle. e.g. She **has finished** her work.',
          'Simple: A basic verb form used to describe facts, habits, or completed actions, without continuous or perfect forms. e.g. She **works** every day. / She **worked** yesterday.',
        ],
      },
    ],
  },
  {
    slug: 'cognate-words',
    title: 'Cognate Words: English and Portuguese',
    blocks: [
      {
        kind: 'p',
        text: 'English and Portuguese are very similar. This is because English is influenced by Latin. Take a look at some cognates between English and Portuguese:',
      },
      {
        kind: 'table',
        header: ['Word ending', 'Examples'],
        rows: [
          ['Words ending in -al → /au/', 'Animal, Natural, Real, Social, Vital, Original, Total'],
          ['Word ending in -ly → /li/', 'Really, Basically, Historically, Automatically, Orally, Naturally'],
          ['Words ending in -ty → /ti/ or /ri/', 'City, University, Honesty, Simplicity, Velocity'],
          ['Words ending in -ent', 'Different, President, Recent, Component, Innocent, Resident, Excellent, Consistent'],
          ['Words ending in -ive → /iv/', 'Positive, Executive, Creative, Competitive, Narrative, Motive, Incentive'],
          ['Words ended in -ous → /us/', 'Religious, Famous, Generous, Ambitious'],
          ['Words ended in -ble → /bou/', 'Possible, Responsible, Impossible, Terrible, Vulnerable, Comfortable'],
          ['Words ended in -tion → /shon/', 'Situation, Communication, Information, Education'],
          ['Words ended in -sion → /jon/', 'Television, Expression, Conclusion, Mission'],
          ['Words ended in -ist', 'Dentist, Journalist, Specialist, Artist, Economist, Feminist'],
        ],
      },
    ],
  },
  {
    slug: 'word-classification',
    title: 'Word Classification',
    blocks: [
      {
        kind: 'p',
        text: 'English has four major word classes: nouns, verbs, adjectives and adverbs. Nouns are the most common type of word, followed by verbs. Adjectives are less common and adverbs are even less common. Besides these 4 classes, prepositions, pronouns, determiners and conjunctions are also very important in sentences.',
      },
      { kind: 'h3', text: '1. NOUN' },
      {
        kind: 'p',
        text: 'A noun refers to a person, animal or thing. Nouns are words that have a lot of meaning and, therefore, are very important in a sentence.',
      },
      {
        kind: 'examples',
        items: [
          'The **woman** in the **picture** is my **mother**.',
          'Her **name** is **Anna**. She’s from **Manchester**.',
        ],
      },
      { kind: 'h3', text: '2. VERB' },
      { kind: 'p', text: 'A verb refers to an action, event or state.' },
      {
        kind: 'examples',
        items: [
          'We **went** home straight after the show. (action)',
          'It **snowed** a lot that winter. (event)',
          'You **are** wonderful. (state)',
        ],
      },
      { kind: 'h3', text: '3. ADJECTIVE' },
      {
        kind: 'p',
        text: 'An adjective gives us more information about people, animals or things represented by nouns and pronouns.',
      },
      {
        kind: 'examples',
        items: [
          'That’s a **big** house.',
          'Look at the **grey** horse.',
          'Some dogs have **long** tails.',
          'She is **tall**.',
        ],
      },
      { kind: 'h3', text: '4. ADVERBS' },
      {
        kind: 'p',
        text: 'We use adverbs to add more information about a verb, an adjective, another adverb, a clause or a whole sentence.',
      },
      {
        kind: 'examples',
        items: [
          'Can you move it **carefully**? It’s fragile.',
          '**Quickly**! We’re late.',
          'She swims **really well**.',
          'Don’t go **so fast**.',
        ],
      },
      { kind: 'h3', text: '5. PREPOSITIONS' },
      {
        kind: 'p',
        text: 'Prepositions describe the relationship between words from the major word classes.',
      },
      {
        kind: 'examples',
        items: [
          'We went **to** the top **of** the mountain. → to describes the relationship between went and top; of describes the relationship between top and mountain',
          'Are you ready **for** lunch yet? → for describes the relationship between ready and lunch',
        ],
      },
      { kind: 'h3', text: '6. PRONOUNS' },
      {
        kind: 'p',
        text: 'Pronouns are words which substitute for noun phrases, so that we do not need to say the whole noun phrase or repeat it unnecessarily.',
      },
      {
        kind: 'examples',
        items: [
          'That’s Gerry in the photo. **He** lives in Barcelona.',
          'This jacket is **mine**. **That** must be Linda’s.',
        ],
      },
      { kind: 'h3', text: '7. DETERMINERS' },
      {
        kind: 'p',
        text: 'Determiners come before nouns. They show what type of reference the noun is making.',
      },
      {
        kind: 'examples',
        items: [
          'Have you got **a** ruler I can borrow?',
          'I need **some** paper for **my** printer.',
          '**This** phone isn’t easy to use.',
        ],
      },
      { kind: 'h3', text: '8. CONJUNCTIONS' },
      {
        kind: 'p',
        text: 'Conjunctions show a link between one word, phrase or clause and another word, phrase or clause.',
      },
      {
        kind: 'examples',
        items: [
          'Joe **and** Dan are brothers.',
          'It was okay, **but** I wouldn’t recommend it as a restaurant.',
        ],
      },
      {
        kind: 'note',
        text: 'Many words belong to more than one word class. For example, book can be used as a noun or as a verb; fast can be used as an adjective or an adverb:',
      },
      {
        kind: 'examples',
        items: [
          'It’s an interesting **book**. (noun)',
          'We ought to **book** a holiday soon. (verb)',
          'He loves **fast** cars. (adjective)',
          'Don’t drive so **fast**! (adverb)',
        ],
      },
    ],
  },
  {
    slug: 'question-words',
    title: 'Question Words',
    blocks: [
      {
        kind: 'p',
        text: 'Question words, or question pronouns, are used in the beginning of open questions. This means that the answer to these questions will never be “yes” or “no”. Let’s take a look at the most used question words.',
      },
      { kind: 'h3', text: '1. What' },
      { kind: 'p', text: 'For a thing, when referring to many things.' },
      {
        kind: 'examples',
        items: ['“**What** did you do this weekend?” “I went to a beautiful park in Montreal.”'],
      },
      { kind: 'h3', text: '2. Which' },
      { kind: 'p', text: 'For a thing, when the person needs to make a choice between a few options.' },
      {
        kind: 'examples',
        items: ['“**Which** gym do you go to?” “I go to Gym Heroes.”'],
      },
      { kind: 'h3', text: '3. Who' },
      { kind: 'p', text: 'For a person.' },
      {
        kind: 'examples',
        items: ['“**Who** is the man having dinner with Kate?” “It’s her new boyfriend!”'],
      },
      { kind: 'h3', text: '4. Where' },
      { kind: 'p', text: 'For a place.' },
      {
        kind: 'examples',
        items: ['“**Where** did you spend your last vacation?” “At home.”'],
      },
      { kind: 'h3', text: '5. Why' },
      { kind: 'p', text: 'For a reason.' },
      {
        kind: 'examples',
        items: ['“**Why** did you decide to move to Canada?” “Because I love the winter!”'],
      },
      { kind: 'h3', text: '6. When' },
      { kind: 'p', text: 'For a time.' },
      {
        kind: 'examples',
        items: ['“**When** did you arrive here?” “I arrived here on the 10th of January, 2019.”'],
      },
      { kind: 'h3', text: '7. How' },
      { kind: 'p', text: 'For a method.' },
      {
        kind: 'examples',
        items: ['“**How** do you go to work?” “I go by bus.”'],
      },
      { kind: 'h3', text: '8. Whose' },
      { kind: 'p', text: 'To ask about possession.' },
      {
        kind: 'examples',
        items: ['“**Whose** jacket is this?” “It’s mine!”'],
      },
    ],
  },
  {
    slug: 'verb-be-affirmative-negative',
    title: 'The Verb Be: Affirmative & Negative Form',
    blocks: [
      {
        kind: 'p',
        text: 'The verb to be is the most important verb in the English language. It is difficult to use because it is an irregular verb in almost all of its forms. We use it with adjectives, age, places, nationalities, and with professions.',
      },
      { kind: 'h3', text: '1. Affirmative' },
      {
        kind: 'p',
        text: 'To make the affirmative form of the verb to be, we use AM, IS and ARE.',
      },
      {
        kind: 'examples',
        items: [
          'I **am** happy. → **I’m** happy.',
          'You **are** students. → **You’re** students.',
          'She/He **is** Brazilian. → **She’s** Brazilian. **He’s** Brazilian.',
          'It **is** hungry. → **It’s** hungry.',
          'We **are** in class. → **We’re** in class.',
          'They **are** tired. → **They’re** tired.',
        ],
      },
      { kind: 'h3', text: '2. Negative' },
      {
        kind: 'p',
        text: 'To make the negative form of the verb to be, we use AM NOT, IS NOT and ARE NOT.',
      },
      {
        kind: 'examples',
        items: [
          'I **am not** happy. → **I’m not** happy.',
          'You **are not** students. → You **aren’t** students.',
          'She/He **is not** Brazilian. → She **isn’t** Brazilian. He **isn’t** Brazilian.',
          'It **is not** hungry. → It **isn’t** hungry.',
          'We **are not** in class. → We **aren’t** in class.',
          'They **are not** tired. → They **aren’t** tired.',
        ],
      },
      {
        kind: 'note',
        text: 'The verb to be is used to indicate age! We DO NOT say “I have 20 years old”.',
      },
      {
        kind: 'examples',
        items: ['She **is** 20 years old. I **am** 40 years old.'],
      },
    ],
  },
  {
    slug: 'verb-be-question-form',
    title: 'The Verb Be: Question Form',
    blocks: [
      {
        kind: 'p',
        text: 'Making questions with the verb be is simple: you need to make an inversion of the verb be and the subject of your sentence.',
      },
      {
        kind: 'examples',
        items: [
          'She **is** from Brazil. → **Is she** from Brazil?',
          'I **am** tired. → **Am I** tired?',
          'You **are** a student. → **Are you** a student?',
          'Beyoncé **is** a great singer → **Is Beyoncé** a great singer?',
        ],
      },
      { kind: 'h3', text: 'Using question words' },
      {
        kind: 'p',
        text: 'When you use question words to make a question, the question word is at the beginning of the question, followed by the verb to be and then the subject.',
      },
      {
        kind: 'examples',
        items: [
          '**Where is** she from?',
          '**How are** you? I **am** tired.',
          '**What are** you? You **are** students.',
          '**What is** your profession? I **am** a teacher.',
          '**Why is** Beyoncé famous? Because she **is** a great singer.',
        ],
      },
    ],
  },
  {
    slug: 'simple-present',
    title: 'The Simple Present',
    blocks: [
      {
        kind: 'p',
        text: 'We use the present simple to talk about habits and routines. Study the examples and then answer the questions below.',
      },
      {
        kind: 'examples',
        items: [
          'I **wake up** at 6 and a half every day.',
          '**Do** you **have** breakfast? I **make** coffee but I **don’t have** breakfast.',
          'Beyoncé **wakes up** at 6 am.',
          '**Does** she **have** breakfast? She **does not have** breakfast or lunch, she **has** brunch.',
        ],
      },
      {
        kind: 'list',
        items: [
          'a. Which subjects take a different ending in positive sentences?',
          'b. Do we make negatives, questions, and short answers the same way for all subjects?',
        ],
      },
      { kind: 'h3', text: 'How do we form the simple present?' },
      {
        kind: 'p',
        text: 'For I, YOU, WE, and THEY, we use the base form of the verb.',
      },
      {
        kind: 'examples',
        items: [
          'I **take** the bus every day.',
          'I **go** to work by car.',
          'I **study** English every day.',
        ],
      },
      {
        kind: 'p',
        text: 'The third person singular (he, she, it) adds an -s, -es or -ies at the end of the verb.',
      },
      {
        kind: 'examples',
        items: [
          'Maria **takes** the bus every day.',
          'She **goes** to work by bus.',
          'She **studies** English every day.',
        ],
      },
      {
        kind: 'note',
        text: 'The verb HAVE is irregular in the simple present. For I, YOU, WE and THEY, we use HAVE. For HE, SHE, and IT we use HAS.',
      },
      { kind: 'h3', text: 'The Simple Present: The Auxiliary Do' },
      {
        kind: 'p',
        text: 'In English, we use auxiliary verbs to form negative and interrogative statements.',
      },
      {
        kind: 'p',
        text: 'For the verb TO BE, we do not use auxiliary verbs to make the negative and question form, because the verb TO BE is its own auxiliary.',
      },
      {
        kind: 'examples',
        items: [
          'You **are** happy. → Subject + main verb + object.',
          'You **are not** happy. → Subject + main verb + not + object.',
          '**Are** you happy? → Main verb + subject + object.',
        ],
      },
      {
        kind: 'p',
        text: 'However, in the Simple Present, we need to use the auxiliary verbs DO and DOES to form the negative and questions.',
      },
      {
        kind: 'list',
        items: [
          'For I, YOU, WE and THEY, we use DO. → DO + NOT = DON’T',
          'For HE, SHE and IT, we use DOES. → DOES + NOT = DOESN’T',
        ],
      },
      {
        kind: 'table',
        header: ['Affirmative', 'Negative', 'Interrogative'],
        rows: [
          ['I **take** one shower a day.', 'I **don’t take** 2 showers a day.', '**Do** I **take** 2 showers a day?'],
          ['You **wake up** at 6 am.', 'You **don’t wake up** at 7 am.', '**Do** you **wake up** at 7 am?'],
          ['She **has** breakfast at 8 am.', 'She **doesn’t have** breakfast.', '**Does** she **have** breakfast?'],
          ['The cat (It) **likes** his home.', 'The cat **doesn’t like** his food.', '**Does** the cat **like** his home?'],
          ['We **work** a lot.', 'We **don’t sleep** a lot.', '**Do** we **work** a lot?'],
          ['They **watch** series at night.', 'They **don’t watch** movies at night.', '**Do** they **watch** series at night?'],
        ],
      },
      {
        kind: 'note',
        text: 'For HE, SHE and IT we need to conjugate the main verb (add -s, -es, and -ies) in the affirmative form. However, in the negative and question form we DO NOT conjugate the main verb. The auxiliary DOES is already conjugated.',
      },
      { kind: 'h3', text: 'The Simple Present: Adding -S, -ES, and -IES' },
      {
        kind: 'p',
        text: 'To conjugate verbs in the simple present, we need to add -s, -es, or -ies to the main verb in the third person singular (he, she, or it).',
      },
      { kind: 'p', text: 'When do we use each of them?' },
      {
        kind: 'p',
        text: '1. Verbs ending in o, ch, sh, th, ss, gh or z, receive -es instead of -s, in the third person.',
      },
      {
        kind: 'examples',
        items: [
          'She **goes** to the movies every Tuesday. (go)',
          'He **watches** TV every day. (watch)',
          'My father always **washes** the dishes. (wash)',
        ],
      },
      {
        kind: 'p',
        text: '2. Verbs ending in y, preceded by a consonant, receive -ies, in the third person.',
      },
      {
        kind: 'examples',
        items: [
          'She **cries** every time she watches Christmas movies. (cry → cries)',
          'Karen **studies** English every day on Duolingo. (study → studies)',
        ],
      },
      {
        kind: 'p',
        text: '3. Verbs ending in y, preceded by a vowel, receive only -s, in the third person.',
      },
      {
        kind: 'examples',
        items: [
          'Marta **plays** soccer for Brazil’s soccer team. (play → plays)',
          'My mom **stays** home every day. (stay → stays)',
        ],
      },
    ],
  },
  {
    slug: 'adverbs-of-frequency',
    title: 'Adverbs of Frequency',
    blocks: [
      {
        kind: 'p',
        text: 'Adverbs of frequency are used to indicate how often something happens.',
      },
      {
        kind: 'examples',
        items: [
          '**How often** do you work out? I work out **twice a week**.',
          '**How often** does Beyoncé work out? Beyoncé works out **every day**.',
        ],
      },
      { kind: 'p', text: 'Here are the most common adverbs of frequency:' },
      {
        kind: 'table',
        header: ['Frequency', 'Adverb', 'Adverb of frequency sentences'],
        rows: [
          ['100%', 'always', 'I **always** have lunch at home.'],
          ['90%', 'usually', 'I **usually** go to the gym on Mondays.'],
          ['80%', 'normally/generally', 'I **normally** do my homework before dinner.'],
          ['70%', 'often', 'She is **often** late to work.'],
          ['50%', 'sometimes', '**Sometimes**, he walks the dog OR He walks the dog **sometimes** OR He **sometimes** walks the dog.'],
          ['30%', 'occasionally', '**Occasionally**, they visit their grandma OR They visit their grandma **occasionally** OR They **occasionally** visit their grandma.'],
          ['5%', 'hardly ever/rarely', 'I **hardly ever** go to the movies.'],
          ['0%', 'never', 'She **never** goes to the beach.'],
        ],
      },
    ],
  },
  {
    slug: 'pronouns-overview',
    title: 'Pronouns: General Overview',
    blocks: [
      {
        kind: 'p',
        text: 'Pronouns replace nouns, which means that they have the value of a noun.',
      },
      {
        kind: 'examples',
        items: [
          'I like that boy. **That boy** is handsome.',
          'I like that boy. **He** is handsome.',
        ],
      },
      { kind: 'p', text: 'He is handsome.' },
      {
        kind: 'list',
        items: ['Subject: He (boy)', 'Verb: to be (is)', 'Object: handsome.'],
      },
      {
        kind: 'examples',
        items: [
          'What do you think about Paul? I like **Paul**. **Paul** is handsome.',
          'What do you think about Paul? I like **him**. **He** is handsome.',
        ],
      },
      { kind: 'p', text: 'I like him.' },
      {
        kind: 'list',
        items: ['Subject: I', 'Verb: like', 'Object: him (Paul).'],
      },
      {
        kind: 'p',
        text: 'In the examples above, the repetition of the word “boy” and “Paul” is tiring for the reader. Therefore, we use pronouns to refer to a noun that has already been presented.',
      },
      { kind: 'h3', text: 'Function of the Pronouns' },
      {
        kind: 'p',
        text: 'A different pronoun is required depending on two elements: the noun being replaced and the function that noun has in the sentence.',
      },
      { kind: 'h3', text: '1. Subject pronouns' },
      {
        kind: 'p',
        text: 'Replace nouns that are the subject of their clause.',
      },
      {
        kind: 'examples',
        items: [
          '**You** are amazing!',
          'Jim is angry, and **he** is not happy.',
        ],
      },
      { kind: 'h3', text: '2. Object pronouns' },
      {
        kind: 'p',
        text: 'Are used to replace nouns that are the direct or indirect object of a verb or clause.',
      },
      {
        kind: 'examples',
        items: [
          'John has a girlfriend. John received a letter from **her** last week.',
          'Jake is hurt because Bill hit **him**.',
        ],
      },
      { kind: 'h3', text: '3. Reflexive pronouns' },
      {
        kind: 'p',
        text: 'Refer back to the subject of the clause because the subject of the action is also the direct or indirect object.',
      },
      {
        kind: 'examples',
        items: [
          'Do you usually cut **yourself** while cooking? I do!',
          'He hurt **himself** on the stairs.',
        ],
      },
      {
        kind: 'note',
        text: 'Reflexive pronouns can be used as intensive pronouns to emphasize the fact that you did something by yourself.',
      },
      {
        kind: 'examples',
        items: [
          'I **myself** made these cookies.',
          'She **herself** designed her graduation dress.',
          'They **themselves** decorated their wedding.',
        ],
      },
      {
        kind: 'table',
        header: ['', 'Subject pronoun', 'Object pronoun', 'Possessive adjective', 'Reflexive pronoun'],
        rows: [
          ['1st person singular', 'I', 'me', 'my', 'myself'],
          ['2nd person singular', 'you', 'you', 'your', 'yourself'],
          ['3rd person singular, male', 'he', 'him', 'his', 'himself'],
          ['3rd person singular, female', 'she', 'her', 'her', 'herself'],
          ['3rd person singular, neutral', 'it', 'it', 'its', 'itself'],
          ['1st person plural', 'we', 'us', 'our', 'ourselves'],
          ['2nd person plural', 'you', 'you', 'your', 'yourselves'],
          ['3rd person plural', 'they', 'them', 'their', 'themselves'],
        ],
      },
    ],
  },
  {
    slug: 'possessive-pronouns-vs-adjectives',
    title: 'Possessive Pronouns vs. Possessive Adjectives',
    blocks: [
      {
        kind: 'p',
        text: '1. A possessive adjective is always followed by a noun: This is my house.',
      },
      {
        kind: 'examples',
        items: [
          'We went to **their** house yesterday.',
          'James never invited me to **his** parties.',
          'Where is **your** umbrella?',
          'The students are doing **their** homework.',
          'You have never visited **our** home.',
        ],
      },
      {
        kind: 'p',
        text: '2. A possessive pronoun is used without a noun, it replaces the noun: That house is mine (my house).',
      },
      {
        kind: 'examples',
        items: [
          'Your car is faster than **hers**. -> My car is faster than her car.',
          'This is my passport. Where is **yours**? -> Where is your passport?',
          'This is not your phone. It is **his**. -> It is his phone.',
          '‘Whose phone is this?’ ‘It is **mine**.’ -> It’s my phone.',
        ],
      },
      {
        kind: 'table',
        header: ['Personal Pronouns', 'Possessive Pronoun', 'Possessive Adjective'],
        rows: [
          ['1st singular -> I', 'mine', 'my'],
          ['2nd -> You', 'yours', 'your'],
          ['3rd (female) -> She', 'hers', 'her'],
          ['3rd (male) -> He', 'his', 'his'],
          ['3rd (neutral) -> It', 'its', 'its'],
          ['1st plural -> We', 'ours', 'our'],
          ['3rd plural -> They', 'theirs', 'their'],
        ],
      },
    ],
  },
  {
    slug: 'relative-pronouns',
    title: 'Relative Pronouns',
    blocks: [
      {
        kind: 'p',
        text: 'Pronouns are words that take the place of a noun. Relative pronouns are used at the beginning of an adjective clause (a dependent clause that modifies a noun). The most common relative pronouns are who, whom, whose, which, and that.',
      },
      {
        kind: 'p',
        text: 'a. Who is used mainly for people and replaces subject pronouns like I, you, she, he, we, they.',
      },
      {
        kind: 'examples',
        items: [
          'It was my husband **who** broke the car door.',
          'Someone broke the car door. My husband broke the car door.',
        ],
      },
      { kind: 'p', text: 'b. Which is used for objects and animals.' },
      {
        kind: 'examples',
        items: [
          'Bridget visited the park with her dog **which** likes to chase squirrels.',
          'Bridget visited the park with her dog. Her dog likes to chase squirrels.',
        ],
      },
      { kind: 'p', text: 'c. That can be used for people, animals, or things.' },
      {
        kind: 'examples',
        items: [
          'The piggy bank **that** was on my desk got broken.',
          'The piggy bank got broken. The piggy bank was on my desk.',
        ],
      },
      {
        kind: 'p',
        text: 'd. Whose indicates possession and relationship. It can be used for people, animals or things:',
      },
      {
        kind: 'examples',
        items: [
          'This is the girl **whose** car I borrowed. → I borrowed the girl’s car.',
          'This is the woman **whose** husband is living abroad. → the woman’s husband',
        ],
      },
      {
        kind: 'p',
        text: 'e. Whom has the same meaning as “who”, but appears after prepositions.',
      },
      {
        kind: 'examples',
        items: [
          'She is the teacher to **whom** I asked a question.',
          'Actually, she knew very little about the man with **whom** she had promised to spend the summer.',
        ],
      },
      {
        kind: 'p',
        text: 'f. When can be a relative pronoun when it introduces clauses that describe time.',
      },
      {
        kind: 'examples',
        items: ['She remembered the day **when** they met.'],
      },
      {
        kind: 'p',
        text: 'g. Where can be a relative pronoun when it introduces clauses that refer to a place.',
      },
      {
        kind: 'examples',
        items: ['The office **where** I work is in this building.'],
      },
      {
        kind: 'p',
        text: 'h. Why can be a relative pronoun when it introduces clauses that refer to a reason.',
      },
      {
        kind: 'examples',
        items: ['You are the reason **why** I left my country.'],
      },
    ],
  },
  {
    slug: 'omission-of-that',
    title: 'The Omission of That',
    blocks: [
      { kind: 'h3', text: '1. As a relative pronoun' },
      {
        kind: 'p',
        text: 'The relative pronoun that can be left out when it is the object in a relative clause.',
      },
      {
        kind: 'examples',
        items: [
          'This is the woman **that** we were talking about. OR This is the woman we were talking about.',
        ],
      },
      { kind: 'h3', text: '2. After reporting verbs' },
      {
        kind: 'p',
        text: 'That can be left out after common reporting verbs like said, thought and suggested.',
      },
      {
        kind: 'examples',
        items: [
          'She said **that** she was busy. OR She said she was busy.',
          'She suggested **that** we should wait. OR She suggested we should wait.',
          'I thought you weren’t interested. OR I thought **that** you weren’t interested.',
        ],
      },
      {
        kind: 'note',
        text: 'That cannot be dropped after verbs like reply and shout.',
      },
      {
        kind: 'examples',
        items: [
          'She replied **that** she wasn’t interested. (NOT She replied she wasn’t interested.)',
          'He shouted **that** he was coming. (NOT He shouted he was coming.)',
        ],
      },
      { kind: 'h3', text: '3. After adjectives' },
      {
        kind: 'p',
        text: 'That can be left out after adjectives in some common expressions.',
      },
      {
        kind: 'examples',
        items: [
          'I’m glad **that** you are safe. OR I’m glad you are safe.',
          'It’s funny **that** he hasn’t come. OR It’s funny he hasn’t come.',
          'I was surprised **that** she won the prize. OR I was surprised she won the prize.',
        ],
      },
    ],
  },
  {
    slug: 'both-neither-either',
    title: 'Both, Neither, and Either',
    blocks: [
      {
        kind: 'p',
        text: 'We use both, either, and neither as pronouns that refer to two people or things.',
      },
      {
        kind: 'p',
        text: '1. Both can be used as a pronoun to refer to two things that we have already mentioned. It includes the two options mentioned. Both = not only X but also Y.',
      },
      {
        kind: 'examples',
        items: [
          '“Do you want the blue shirt or the red one?” “I’ll buy **both**.”',
          'I’m **both** happy and confused at the same time!',
          'She speaks **both** English and Arabic.',
          '**Both** (of) my friends arrived late to class.',
        ],
      },
      {
        kind: 'p',
        text: '2. Neither is a negative word and is accompanied by an affirmative singular verb. Neither = not one and not the other',
      },
      {
        kind: 'examples',
        items: [
          'That tennis game was very close. **Neither** player had a clear advantage.',
          '**Neither** of the parents understood what the baby was trying to say.',
          '“I have never been to Switzerland.” “**Neither** have I.”',
          'I have **neither** time nor money.',
        ],
      },
      {
        kind: 'p',
        text: '3. Either is accompanied by an affirmative singular verb and is mostly used in questions or negative sentences. Either = any one of the two = this one or the other one',
      },
      {
        kind: 'examples',
        items: [
          '**Either** you leave the building now or I call the security guards.',
          'There are only two options and I’m not interested in **either** film.',
          'I don’t think he is going to invite **either** of us.',
          '“I’ve never been to Portugal.” “I haven’t **either**.”',
          '“I have been to Quebec.” “I have **either**.” OR “I have so too”',
        ],
      },
    ],
  },
  {
    slug: 'demonstrative-adjectives',
    title: 'Demonstrative Adjectives: This, These, That & Those',
    blocks: [
      {
        kind: 'p',
        text: 'The four English demonstrative pronouns are this, that, these, and those. They are used to replace something that was mentioned before.',
      },
      {
        kind: 'p',
        text: 'Demonstrative pronouns “demonstrate” something. Using them is equivalent to pointing at something or someone.',
      },
      {
        kind: 'examples',
        items: [
          '**This** house is beautiful. → you are in front of the house.',
          '**That** house is beautiful. → you are far from the house.',
          '**These** houses are beautiful. → you are near to the houses.',
          '**Those** houses are beautiful. → you are far from the houses.',
        ],
      },
      {
        kind: 'table',
        header: ['', 'Near (in time or distance)', 'Far (in time or distance)'],
        rows: [
          ['Singular', 'this', 'that'],
          ['Plural', 'these', 'those'],
        ],
      },
    ],
  },
  {
    slug: 'there-is-there-are',
    title: 'There Is & There Are',
    blocks: [
      {
        kind: 'p',
        text: '“There is” and “there are” are used to indicate the existence of something, as in Portuguese “há.”',
      },
      {
        kind: 'p',
        text: 'There is should be used when the noun that follows is in the singular.',
      },
      {
        kind: 'examples',
        items: [
          '**There is** a cat in my living room.',
          '**There is** a kitchen, a living room, and a bedroom in my apartment.',
        ],
      },
      {
        kind: 'p',
        text: 'There are should be used when the noun that follows is in the plural.',
      },
      {
        kind: 'examples',
        items: [
          '**There are** many people bothered by my cats’ meowing.',
          '**There are** a lot of amazing women in sports',
        ],
      },
    ],
  },
  {
    slug: 'prepositions-of-place',
    title: 'Prepositions of Place: In, On, At',
    blocks: [
      {
        kind: 'p',
        text: 'Prepositions can be very confusing for English learners. Most of the time, we learn prepositions using them and not by learning rules. This is because there are a lot of exceptions to the rule. However, some general rules can help you. In general, we use:',
      },
      {
        kind: 'h3',
        text: 'In for an ENCLOSED SPACE (and also cities, states, countries, etc.)',
      },
      {
        kind: 'examples',
        items: [
          'Do you work **in** a laboratory? - Enclosed space',
          'I have a meeting **in** New York. - City',
          'Do you live **in** Japan? - Country',
          'Jupiter is **in** the Solar System. - Big ‘places’',
        ],
      },
      { kind: 'h3', text: 'On for a SURFACE' },
      {
        kind: 'examples',
        items: [
          'The book is **on** the table.',
          'The author\'s name is **on** the cover of the book.',
          'There are no prices **on** this menu.',
          'You are standing **on** my foot.',
          'There was a "no smoking" sign **on** the wall.',
          'I live **on** the 7th floor at 21 Oxford Street in London.',
        ],
      },
      { kind: 'h3', text: 'At for a POINT (usually specific places)' },
      {
        kind: 'examples',
        items: [
          'Jane is waiting for you **at** the bus stop.',
          'The shop is **at** the end of the street.',
          'When will you arrive **at** our house?',
        ],
      },
      {
        kind: 'table',
        header: ['at (POINT)', 'in (ENCLOSED SPACE)', 'on (SURFACE)'],
        rows: [
          ['at the corner', 'in the garden', 'on the wall'],
          ['at the bus stop', 'in London', 'on the ceiling'],
          ['at the door', 'in France', 'on the door'],
          ['at the top of the page', 'in a box', 'on the cover'],
          ['at the end of the road', 'in my pocket', 'on the floor'],
          ['at the entrance', 'in my wallet', 'on the carpet'],
          ['at the crossroads', 'in a building', 'on the menu'],
          ['at the front desk', 'in a car', 'on a page'],
          ['at home', 'in a car', 'on a bus'],
          ['at work', 'in a taxi', 'on a train'],
          ['at school', 'in a helicopter', 'on a plane'],
          ['at university', 'in a boat', 'on a ship'],
          ['at college', 'in a lift (elevator)', 'on a bicycle, on a motorbike'],
          ['at the top', 'in the newspaper', 'on a horse, on an elephant'],
          ['at the bottom', 'in the sky', 'on the radio, on television'],
          ['at the side', 'in a row', 'on the left, on the right'],
        ],
      },
    ],
  },
  {
    slug: 'prepositions-of-time',
    title: 'Prepositions of Time: In, On, At',
    blocks: [
      { kind: 'h3', text: 'IN' },
      {
        kind: 'p',
        text: 'We usually use in with parts of the day and longer periods of time such as months, seasons and years.',
      },
      {
        kind: 'examples',
        items: [
          'I usually relax **in** the evening.',
          '**In** summer it\'s too hot to do anything.',
          'I\'m always really busy **in** December.',
        ],
      },
      { kind: 'h3', text: 'ON' },
      { kind: 'p', text: 'We usually use on with days and dates.' },
      {
        kind: 'examples',
        items: [
          '**On** Fridays, I have a long lunch.',
          'It\'s his birthday **on** 19 October.',
        ],
      },
      { kind: 'h3', text: 'AT' },
      { kind: 'p', text: 'We usually use at with clock times.' },
      {
        kind: 'examples',
        items: [
          'I get up **at** 6.30 a.m. and go for a run.',
          'She doesn\'t like to leave the office **at** lunchtime.',
        ],
      },
      {
        kind: 'note',
        text: 'We say at night when we mean all of the night. But we say in the night when we want to talk about a specific time during the night.',
      },
      {
        kind: 'examples',
        items: [
          'She\'s a nurse and she works **at** night.',
          'The baby often wakes up **in** the night.',
        ],
      },
      { kind: 'h3', text: 'Long Holidays' },
      {
        kind: 'p',
        text: 'We use at with Christmas and other holidays that last several days.',
      },
      {
        kind: 'examples',
        items: [
          'The weather is very cold here **at** Christmas.',
          '**At** Chinese New Year, many people go home to their families.',
        ],
      },
      {
        kind: 'table',
        header: ['Preposition', 'Time', 'Example'],
        rows: [
          ['AT: Use at with times', 'at 12:00', 'Brad usually eats lunch **at** 12:00.'],
          ['Other common expressions with at', 'at night', 'They often go out **at** night.'],
          ['', 'at Christmas (during a period of time around Christmas, for example, December 23rd-28th)', 'We visit our family and friends **at** Christmas.'],
          ['', 'at the age of...', 'I moved to California **at** the age of two.'],
          ['ON: Use on with dates and days', 'on January 18th', 'Pedro\'s birthday is **on** January 18th.'],
          ['', 'on Saturday evenings', 'We usually go out **on** Saturday evenings.'],
          ['', 'on Christmas day (on December 25th)', 'We have a special meal **on** Christmas day.'],
          ['IN: Use in + a period of time = a time in the future', 'in a few minutes', 'The bus will be leaving **in** a few minutes.'],
          ['', 'in five weeks also: in five weeks\' time', 'I learned to ride a bicycle **in** five weeks. We will start summer vacation **in** five weeks\' time.'],
          ['Use in for longer periods of time', 'in the 1960s', 'The astronauts explored the moon **in** the 1960s.'],
          ['', 'in the 20th century', 'Automobiles and airplanes became very popular **in** the 20th century.'],
          ['', 'in December', '**In** December, you will study less.'],
          ['', 'in 2010', 'The Winter Olympics were held in Vancouver **in** 2010.'],
        ],
      },
    ],
  },
];
